import { Button, Spin } from "antd";

type AuthState =
  | "checking-session"
  | "already-authenticated"
  | "confirm"
  | "signing-in"
  | "redirecting"
  | "error"
  | "invalid";

interface AuthRedirectViewProps {
  authState: AuthState;
  error?: string;
  existingEmail?: string;
  magicLinkEmail?: string | null;
  onContinue: () => void;
  onSignOutAndContinue: () => void;
  onGoToDashboard: () => void;
}

const pageStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#001529",
  zIndex: 1000,
};

const cardStyle: React.CSSProperties = {
  padding: "48px 64px",
  width: 420,
  minHeight: 300,
  backgroundColor: "#fff",
  borderRadius: 5,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const contentStyle: React.CSSProperties = {
  marginTop: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 24,
  width: "100%",
};

function Logo() {
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <img
        alt="Clipboard Health"
        height={60}
        src={`${import.meta.env.BASE_URL}justClipboardBlackText.png`}
      />
      <img alt="" height={65} src={`${import.meta.env.BASE_URL}bunny.png`} />
    </div>
  );
}

export function AuthRedirectView({
  authState,
  error,
  existingEmail,
  magicLinkEmail,
  onContinue,
  onSignOutAndContinue,
  onGoToDashboard,
}: AuthRedirectViewProps) {
  if (authState === "checking-session") {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Logo />
          <div style={contentStyle}>
            <Spin spinning tip={<span style={{ fontSize: 16 }}>Loading...</span>}>
              <div style={{ padding: "50px" }} />
            </Spin>
          </div>
        </div>
      </div>
    );
  }

  if (authState === "already-authenticated") {
    const isSameUser =
      existingEmail &&
      magicLinkEmail &&
      existingEmail.toLowerCase() === magicLinkEmail.toLowerCase();

    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Logo />
          <div style={contentStyle}>
            <p style={{ textAlign: "center", margin: 0, fontSize: 16, color: "#333" }}>
              You are already signed in as <strong>{existingEmail}</strong>.
            </p>
            <Button block type="primary" size="large" onClick={onGoToDashboard}>
              Go to Dashboard
            </Button>
            <Button block size="large" onClick={onSignOutAndContinue}>
              {isSameUser
                ? "Sign out and use this link"
                : `Sign in as ${magicLinkEmail ?? "a different user"}`}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (authState === "invalid" || authState === "error") {
    const message =
      authState === "invalid"
        ? "Invalid or missing authentication link. Please request a new login link."
        : error;

    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Logo />
          <div style={{ ...contentStyle, marginTop: 32 }}>
            <p style={{ color: "#ff4d4f", textAlign: "center", margin: 0, fontSize: 16 }}>
              {message}
            </p>
            <Button block href={`${import.meta.env.BASE_URL}enter`} type="primary" size="large">
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (authState === "signing-in" || authState === "redirecting") {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <Logo />
          <div style={contentStyle}>
            <Spin
              spinning
              tip={
                <span style={{ fontSize: 16 }}>
                  {authState === "redirecting" ? "Redirecting..." : "Signing In..."}
                </span>
              }
            >
              <div style={{ padding: "50px" }} />
            </Spin>
          </div>
        </div>
      </div>
    );
  }

  // authState === "confirm"
  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <Logo />
        <div style={contentStyle}>
          <Button block type="primary" size="large" onClick={onContinue}>
            Continue to Clipboard
          </Button>
        </div>
      </div>
    </div>
  );
}
